import * as THREE from "three";
import { Heading, RoadMap, headingBetween } from "@highmarch/domain";
import { Palette, cellToWorld } from "../scene";
import { AnimRig } from "../rig";
import { Terrain, groundAt } from "../terrain";
import { GroundMesh } from "../groundMesh";
import { BridgeView } from "./bridgeView";
import { RoadDecor } from "../skins/kit/roadDecor";
import { WornSkins } from "../skins/wornSkins";
import { HQ_PATH, HQ_PORTAL, HQ_SPECIAL, HQ_YAW, SURFACE_Y, TILE_TINT, cube, lib, roadY, tileMarker, yawFor } from "./common";

/**
 * Road tiles oriented from the road order, special tiles (mud, bridge, toll, fire trap) on their own HQ pieces where
 * one exists for the shape, and the enemy portal at the open end (HQ arch + spinning ring, or a plain torus). Without the HQ path pieces the road
 * is plain blocks.
 */
export class RoadView {
  readonly group = new THREE.Group();
  private readonly ring: THREE.Mesh;
  private rigs: AnimRig[] = [];
  private spin = 0;
  /**
   * The level's enemy slots, so the plank bridges can give under the units crossing them (set once by the level).
   * Only `alive` and the position are read.
   */
  enemies: readonly { readonly alive: boolean; readonly x: { toNumber(): number }; readonly y: { toNumber(): number } }[] | null = null;
  /** The plank bridges over the gorges the road crosses (bridgeView.ts). */
  private readonly bridges = new BridgeView();
  /** The worn road skin's props that answer the enemies (RoadPiece.react), or null. */
  private decor: RoadDecor | null = null;

  constructor() {
    this.ring = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.06, 8, 24), new THREE.MeshBasicMaterial({ color: 0xc46bff }));
    this.ring.rotation.x = Math.PI / 2;
  }

  /** Leaving the level: the portal ring is the only geometry and material this view makes itself. */
  dispose(): void { this.bridges.dispose(); this.group.clear(); this.ring.geometry.dispose(); (this.ring.material as THREE.Material).dispose(); }

  rebuild(road: RoadMap): void {
    this.group.clear();
    this.rigs = [];
    this.decor = null;
    const L = lib();
    const cells = road.roadCells; // portal … base
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (c.equals(road.grid.base)) continue;
      const prev = i > 0 ? cells[i - 1] : null;
      const next = cells[i + 1];
      // without the library or the HQ path pieces the road is plain blocks
      if (!L || !L.hq(HQ_PATH.straight)) { if (!L || !GroundMesh.available) this.group.add(cube(cellToWorld(c, 0.02), 1, 0.08, 1, Palette.designed)); continue; }
      const outH = headingBetween(c, next);
      const inH = prev ? headingBetween(prev, c) : outH;
      const kind = road.roadTileKinds[i];
      const special = HQ_SPECIAL[kind];
      let tile: THREE.Object3D;
      let dedicated = false;
      let model = "";
      if (prev === null) {
        // the end piece opens toward +Z (after HQ_YAW); the road continues toward `next`.
        tile = L.clone(`hq:${HQ_PATH.end}`, TILE_TINT[kind]);
        tile.rotation.y = yawFor[outH] + HQ_YAW;
      } else if (inH === outH) {
        dedicated = !!special && L.hq(special.straight);
        model = dedicated ? `hq:${special!.straight}` : `hq:${HQ_PATH.straight}`;
        tile = L.clone(model, dedicated ? undefined : TILE_TINT[kind]);
        tile.rotation.y = yawFor[outH] + HQ_YAW;
      } else {
        dedicated = !!special && special.corner !== null && L.hq(special.corner);
        model = dedicated ? `hq:${special!.corner}` : `hq:${HQ_PATH.corner}`;
        tile = L.clone(model, dedicated ? undefined : TILE_TINT[kind]);
        // The raw HQ corner opens −z and +x; a −π/2 turn makes it open north+east, which is what cornerYaw's table assumes.
        tile.rotation.y = cornerYaw(inH, outH) - Math.PI / 2;
      }
      // relief: the ground rolls, so the road simply follows it. The one real object is a plank span over a chasm,
      // and that is added once per span below rather than once per cell.
      const T = Terrain.current;
      const bridge = T?.bridgeAt(c) ?? null;
      tile.position.copy(cellToWorld(c, groundAt(c.x, c.y)));
      // When the ground mesh paints the track, only the pieces that are real objects survive — and over a chasm the
      // real object is the plank span added below, not a path tile laid on top of it.
      const painted = GroundMesh.available && !dedicated;
      if (!painted) this.group.add(tile);
      if (dedicated) this.animate(tile, model);
      else { const marker = tileMarker(kind); if (marker) { marker.position.copy(cellToWorld(c, groundAt(c.x, c.y) + 0.02)); this.group.add(marker); } }
    }
    // the plank bridges, fitted to the gorges as drawn (the ground is built before the road views rebuild)
    this.bridges.rebuild();
    this.group.add(this.bridges.group);
    // the worn road skin's dressing along the built road (render/skins/kit/roadDecor.ts)
    // (bare: a plank bridge, or a special tile on its own model)
    const T3 = Terrain.current, kinds = road.roadTileKinds;
    this.decor = RoadDecor.build(this.group, WornSkins.of("road"), cells, (i) => (kinds[i] ?? 0) !== 0 || !!T3?.bridgeAt(cells[i]), GroundMesh.drawnAt);
    const portal = road.portal;
    // Before the first road card the road is only the base cell, and `road.portal` IS the base: the arch would stand
    // inside the fortress. The portal appears once the road has left the base.
    this.ring.visible = false;
    if (!portalShown(road)) return;
    const firstH = cells.length > 1 ? headingBetween(cells[0], cells[1]) : Heading.North;
    if (L && L.hq(HQ_PORTAL.base)) {
      const arch = L.clone(`hq:${HQ_PORTAL.base}`);
      arch.rotation.y = yawFor[firstH] + HQ_YAW;
      arch.position.copy(cellToWorld(portal, groundAt(portal.x, portal.y)));
      this.group.add(arch);
      this.animate(arch, `hq:${HQ_PORTAL.base}`);
      if (L.hq(HQ_PORTAL.ring)) {
        const ring = L.clone(`hq:${HQ_PORTAL.ring}`);
        ring.rotation.y = arch.rotation.y;
        ring.position.copy(arch.position);
        this.group.add(ring);
        this.animate(ring, `hq:${HQ_PORTAL.ring}`);
      }
      this.ring.visible = false;
      return;
    }
    this.ring.visible = true;
    this.ring.position.copy(cellToWorld(portal, SURFACE_Y + 0.5));
    this.group.add(this.ring);
  }

  /** HQ props with an `idle` clip (portal runes, fire-trap flames, toll lamps) keep playing it. */
  private animate(model: THREE.Object3D, name: string): void {
    const L = lib();
    const rig = new AnimRig(model, L ? L.clipsOf(name) : []);
    rig.playLoop("idle", 1, true);
    this.rigs.push(rig);
  }

  update(dt: number): void {
    this.spin += dt;
    for (const r of this.rigs) r.update(dt);
    this.bridges.update(dt, this.enemies);
    this.decor?.update(dt, this.enemies);
    if (!this.ring.visible) return;
    this.ring.rotation.z = this.spin;
    this.ring.position.y = SURFACE_Y + 0.5 + Math.sin(this.spin * 2) * 0.05;
  }
}

// the bridges' sag lives with the bridges; the units and the tests still find it here
export { BridgeSag, bridgeSag, deckSagAt } from "./bridgeView";

/** Whether the road has an open end away from the base for the enemy portal to stand on. */
export function portalShown(road: Pick<RoadMap, "portal" | "roadCells" | "grid">): boolean {
  return road.roadCells.length > 1 && !road.portal.equals(road.grid.base);
}

/**
 * A corner piece joining the +X (East) and +Z (North) sides at rotation 0. A rotation of θ about Y maps
 * (x, z) → (x cos θ + z sin θ, −x sin θ + z cos θ): π/2 joins East+South, π joins South+West, −π/2 joins North+West.
 */
function cornerYaw(inH: Heading, outH: Heading): number {
  const opposite = ((inH + 2) & 3) as Heading;
  const key = [opposite, outH].sort((a, b) => a - b).join(",");
  const table: Record<string, number> = {
    [`${Heading.North},${Heading.East}`]: 0, [`${Heading.East},${Heading.South}`]: Math.PI / 2,
    [`${Heading.South},${Heading.West}`]: Math.PI, [`${Heading.North},${Heading.West}`]: -Math.PI / 2,
  };
  return table[key] ?? 0;
}
